import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import useQuoteForm from '../hooks/useQuoteForm';
import useQuoteProcessor from '../hooks/useQuoteProcessor';
import { mapAircraftToPayload } from '../utils/transformBidData';
import { getQuoteStatusConfig, getEffectiveQuoteStatus } from '../utils';
import { Skeleton } from '@components/ui/Skeleton';
import Modal from '@components/ui/Modal';
import { RiLoader4Line, RiErrorWarningLine } from '@remixicon/react';
import ClientInformationSection from '../components/generate/ClientInformationSection';
import FlightDetailsSection from '../components/generate/FlightDetailsSection';
import AircraftOptionsSection from '../components/generate/AircraftOptionsSection';
import QuoteSettingsSection from '../components/generate/QuoteSettingsSection';
import QuotePreview from '../components/generate/QuotePreview';
import QuoteCustomizeModal from '../components/generate/QuoteCustomizeModal';
import { resolveAssetUrl, trunc2 } from '@/utils';
import { normalizeRole, ROLES } from '@/lib/permissions';
import { usePlatformFeeRate } from '@/hooks/usePlatformFeeRate';
import {
  getLegMultiplier,
  computeForwardPricing,
  isPricingInconsistent,
  makeHistoricalRateResolver,
} from '../utils/pricingForwardCalc';

/**
 * GenerateQuote Page
 * Main page for generating and sending quote quotes
 */
const GenerateQuote = () => {
  const [searchParams] = useSearchParams();
  const isQuickQuote = searchParams.has('quickquote');
  const editQuoteId = searchParams.get('quoteid');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  // Tracks the last submit action so the error-banner retry continues the same
  // flow: 'draft' → re-run the draft save; 'send' → reopen the email composer.
  const [lastSubmitMode, setLastSubmitMode] = useState('send');
  // Edit-mode Send confirmation: asks whether to resend the email to the
  // client (open the composer) or just save silently (PATCH only).
  const [editSendConfirmOpen, setEditSendConfirmOpen] = useState(false);
  // Themed validation modal: surfaces missing/invalid fields and jumps to
  // the first failing section instead of silently dropping an inline error.
  const [validationModal, setValidationModal] = useState({ open: false, issues: [] });
  // Pricing-inconsistency modal: fires on Next from the Aircraft section
  // when any bid_option's persisted final_price doesn't match the
  // forward-calc result (TR-2610-01318 class — FET stored but
  // final_price stale; also catches duplicates of broken quotes).
  // Broker clicks OK → every inconsistent option is recomputed into
  // formData in-place and the Aircraft section stays open for review.
  // Acknowledged flag so a second Next click proceeds normally even
  // if the broker didn't open Edit Pricing.
  const [pricingInconsistencyOpen, setPricingInconsistencyOpen] = useState(false);
  const [pricingAcknowledged, setPricingAcknowledged] = useState(false);
  const { shortlistAircraft, selectedTripGroup, clearShortlist, clearSelectedTripGroup, removeFromShortlist } = useAppStore();
  const { userProfile } = useAuthStore();

  const { data: organization } = useQuery({
    queryKey: ['organization', userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  // Edit mode: fetch the source quote when ?quoteid= is present, then
  // hydrate the form below.
  const { data: editSource } = useQuery({
    queryKey: ['quoteDetails', editQuoteId],
    queryFn: () => apiClient.getQuoteById(editQuoteId),
    enabled: !!editQuoteId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Cached airport list — used to enrich each leg's airport object during
  // hydration so LocationInput shows "KOPF - Miami-Opa Locka Exec" instead
  // of just the bare ICAO code.
  // Airports for the leg-enrichment step now live inside loadFromQuote —
  // it fetches only the specific ICAO codes on this quote's legs
  // (parallel getAirportByIcao calls, deduped) instead of pulling the
  // whole airport list up-front.

  const {
    formData,
    updateClient,
    updateFlights,
    updateSettings,
    updateBidOptions,
    updateBidOptionProperty,
    updateBidOptionProperties,
    updateAircraftProfile,
    updateOperatorNameOverride,
    removeBidOption,
    toggleSection,
    goToNextSection,
    goToPreviousSection,
    expandedSections,
    unlockedSections,
    completedSections,
    resetForm,
    getFormErrors,
    getValidationIssues,
    openSection,
    loadFromQuote,
    isEditing,
  } = useQuoteForm();

  const {
    handleSaveDraft,
    handleSubmitForApproval,
    handleSendQuote,
    handleUpdateQuote,
    handleUpdateQuoteAndNotify,
    isSending,
    isSavingDraft,
    error,
    setError,
  } = useQuoteProcessor(organization);

  // Hydrate the form once the edit source is loaded. loadFromQuote is
  // now async — it fetches airport records for the specific ICAOs on
  // this quote's legs so LocationInput renders "ICAO - Name" pairs.
  useEffect(() => {
    if (editSource) {
      loadFromQuote(editSource);
    }
  }, [editSource, loadFromQuote]);

  // Pricing-inconsistency detection — fires on Next from the Aircraft
  // section when any bid_option's persisted final_price doesn't match
  // the forward-calc result (TR-2610-01318 class: FET stored but
  // final_price stale, duplicates of broken quotes carrying stale
  // numbers forward). Formulas live in the shared pricingForwardCalc
  // util so rate changes propagate everywhere.
  //
  // Platform-fee rate resolution is status-dependent:
  //
  // DRAFT / PENDING_APPROVAL (pre-send): use the live admin rate
  //   so a rate change applies to new quotes going forward.
  //
  // SENT and beyond: back-solve the historical rate per option from
  //   its stored platform_fee. A mid-cycle admin rate change can't
  //   silently re-price what the client has already seen.
  //
  // Fresh (no editSource — the Quick Quote flow) is pre-send by
  // definition.
  const { resolveForSubtotal } = usePlatformFeeRate();
  const isPreSendForPricing = (() => {
    const status = (editSource?.status || 'DRAFT').toUpperCase();
    return ['DRAFT', 'PENDING_APPROVAL'].includes(status);
  })();

  const makePricingContext = useCallback((opt) => ({
    passengerCount: formData?.flights?.flights?.[0]?.passengerCount || 1,
    legMultiplier: getLegMultiplier(
      formData?.flights?.type || 'one-way',
      formData?.flights?.flights?.length || 1,
    ),
    resolvePlatformFeeRate: isPreSendForPricing
      ? resolveForSubtotal
      : makeHistoricalRateResolver(opt),
  }), [formData?.flights, resolveForSubtotal, isPreSendForPricing]);

  const inconsistentOptionIds = useMemo(() => {
    const options = formData?.bidOptions || [];
    if (options.length === 0) return [];
    return options
      .filter((opt) => isPricingInconsistent(opt, makePricingContext(opt)))
      .map((opt) => opt.bid_option_id);
  }, [formData?.bidOptions, makePricingContext]);

  // Reset acknowledgment when inconsistencies change (e.g., broker
  // fixed one via Edit Pricing drawer; a different quote loaded).
  useEffect(() => {
    if (inconsistentOptionIds.length === 0) setPricingAcknowledged(false);
  }, [inconsistentOptionIds.length]);

  // Recompute every inconsistent bid_option in-place via the batch
  // update helper. The AircraftCard re-renders with the corrected
  // Final Price immediately — no save needed yet; broker can review
  // the card and click Edit Pricing if they want finer control.
  //
  // For sent quotes we deliberately omit `platform_fee` from the
  // batch update so the stored dollar amount is preserved verbatim
  // — the historical rate resolver recomputes to the same value
  // anyway, but omitting it is an extra belt + suspenders.
  const applyPricingRecompute = () => {
    (formData?.bidOptions || []).forEach((opt) => {
      if (!inconsistentOptionIds.includes(opt.bid_option_id)) return;
      const { fetEff, segEff, intlEff, platformFeeEff, expectedFinal } = computeForwardPricing(
        opt,
        makePricingContext(opt),
      );
      const patch = {
        federal_excise_tax: trunc2(fetEff).toFixed(2),
        segment_tax: trunc2(segEff).toFixed(2),
        international_tax: trunc2(intlEff).toFixed(2),
        final_price: expectedFinal.toFixed(2),
        total_price: expectedFinal.toFixed(2),
      };
      if (isPreSendForPricing) {
        patch.platform_fee = trunc2(platformFeeEff).toFixed(2);
      }
      updateBidOptionProperties(opt.bid_option_id, patch);
    });
    setPricingAcknowledged(true);
    setPricingInconsistencyOpen(false);
    // Keep the broker on the Aircraft section so they can review the
    // updated numbers before clicking Next again.
    openSection('aircraft');
  };

  // Wrapper around goToNextSection('aircraft') — intercepts the Next
  // click when the quote has pricing inconsistencies and the broker
  // hasn't acknowledged yet. Second Next click (after OK) proceeds
  // normally because pricingAcknowledged flips to true.
  const handleAircraftNext = () => {
    if (inconsistentOptionIds.length > 0 && !pricingAcknowledged) {
      setPricingInconsistencyOpen(true);
      return;
    }
    goToNextSection('aircraft');
  };

  // Edit-mode shortcut: brokers can click any unlocked section
  // header directly instead of using Next. Intercept the Quote
  // Settings expand (last section) so a broker jumping straight
  // past Aircraft Pricing still hits the inconsistency popup.
  // Only intercepts the EXPAND path — collapsing Settings is fine.
  const handleSettingsToggle = () => {
    const isExpanding = !expandedSections.settings;
    if (
      isExpanding
      && inconsistentOptionIds.length > 0
      && !pricingAcknowledged
    ) {
      setPricingInconsistencyOpen(true);
      return;
    }
    toggleSection('settings');
  };

  // Lock the Client Information section in edit mode once the quote has been
  // sent to the client — changing the client mid-flow would orphan the
  // already-delivered email and confuse the recipient.
  const isClientEditLocked = (() => {
    const status = (editSource?.status || '').toUpperCase();
    return isEditing && ['SENT', 'VIEWED', 'SIGNED'].includes(status);
  })();
  // TEMP: surface the lock decision to make miswiring easy to spot.
  if (editSource) {
    // eslint-disable-next-line no-console
    console.log('[GenerateQuote] edit lock check', {
      isEditing,
      sourceStatus: editSource?.status,
      isClientEditLocked,
    });
  }

  const isDataLoading = !shortlistAircraft && !selectedTripGroup;

  // Memoize allBidOptions to prevent unnecessary recalculations
  const allBidOptions = React.useMemo(() => {
    if (shortlistAircraft && shortlistAircraft.length > 0) {
      return shortlistAircraft.map(mapAircraftToPayload);
    }
    return selectedTripGroup?.bids?.flatMap(bid => bid.bid_options || []) || [];
  }, [shortlistAircraft, selectedTripGroup]);

  // Reset form when component mounts or navigation changes.
  // Skipped in edit mode — the form must hydrate from the source quote
  // (handled by the loadFromQuote effect above), not be cleared.
  //
  // Also reset the shortlist-tracking refs AND flip
  // `justResetQuickQuoteRef` so the init-load effect (declared later
  // in source order) can detect that we're on a fresh Quick Quote
  // mount and skip re-stamping the stale shortlist back into
  // formData.bidOptions. Without this, a broker who navigated away
  // (e.g. to /organizations to tune platform fee brackets) and
  // returned to Quick Quote would see the previous aircraft silently
  // re-added, with every other form field cleared — mismatched state.
  useEffect(() => {
    if (editQuoteId) return;
    if (isQuickQuote) {
      clearShortlist();
      clearSelectedTripGroup();
      resetForm();
      shortlistProcessedIdsRef.current = new Set();
      initialLoadDoneRef.current = false;
      justResetQuickQuoteRef.current = true;
    } else {
      resetForm({ preserveBidOptions: !!selectedTripGroup });
    }
  }, [searchParams]);

  // Track aircraft IDs we've already integrated into formData.bidOptions
  // so subsequent shortlist adds (via Add Aircraft → Search) can be
  // appended rather than restarting the initial load. Also gates the
  // initialization effect below to fire only once — otherwise a fresh
  // shortlist entry from the "Add Aircraft" flow re-runs the effect and
  // clobbers pre-existing tripGroup / DB-hydrated tails.
  const shortlistProcessedIdsRef = React.useRef(new Set());
  const initialLoadDoneRef = React.useRef(false);
  // Set to true by the Quick Quote mount reset effect, cleared on the
  // first init-load pass that would otherwise fire with a stale
  // shortlist closure. See the reset effect above for rationale.
  const justResetQuickQuoteRef = React.useRef(false);

  // Initialize bidOptions ONCE when the initial source (shortlist or trip
  // group) is ready. Skipped in edit mode — bid options come from
  // loadFromQuote. Post-initial-load shortlist additions are handled by
  // the append-consumer effect below.
  useEffect(() => {
    if (editQuoteId) return;
    // Mount-fresh Quick Quote: the sibling reset effect above cleared
    // shortlistAircraft but React hasn't committed that state yet, so
    // `allBidOptions` still reflects the previous session's shortlist.
    // Skip exactly one pass so we don't silently re-stamp the stale
    // aircraft back into bidOptions after resetForm wiped it.
    if (justResetQuickQuoteRef.current) {
      justResetQuickQuoteRef.current = false;
      return;
    }
    if (initialLoadDoneRef.current) return;
    if (allBidOptions.length > 0 && !isDataLoading) {
      updateBidOptions(prev => {
        if (!prev || prev.length === 0) return allBidOptions;
        const existingMap = new Map(prev.map(opt => [opt.bid_option_id, opt]));
        return allBidOptions.map(opt => {
          const existing = existingMap.get(opt.bid_option_id);
          return existing ? { ...opt, ...existing } : opt;
        });
      });
      // Mark every initially-loaded aircraft_id as "already processed" so
      // the append-consumer below doesn't re-add them.
      allBidOptions.forEach((opt) => {
        const aid = opt?.aircraft?.aircraft_id || opt?.aircraft_id;
        if (aid) shortlistProcessedIdsRef.current.add(aid);
      });
      initialLoadDoneRef.current = true;
      // Drain the shortlist AFTER handing its contents off to
      // formData.bidOptions — but ONLY when we're NOT on the Quick
      // Quote path. `processQuote` uses `shortlistAircraft.length > 0`
      // as the routing signal for Path 1 (create fresh bid + POST
      // bid_options). Clearing here would push the save into Path 2
      // (updateExistingBidOptions → PATCH by the placeholder
      // bid_option_id → 404). Trip-group already arrives with an
      // empty shortlist (the drawer clears it before navigating), so
      // this is a no-op there; edit mode returns early above and
      // never reaches this branch. The Quick Quote reset effect
      // above drains the shortlist on the next fresh mount.
      if (!isQuickQuote && shortlistAircraft && shortlistAircraft.length > 0) {
        clearShortlist();
      }
    }
  }, [allBidOptions, isDataLoading, updateBidOptions, editQuoteId, isQuickQuote, shortlistAircraft, clearShortlist]);

  // Append a freshly-created aircraft (from AddAircraftManuallyDrawer or
  // the SearchAircraft → Add Tail path) as a bid_option on this quote.
  //
  // Two paths:
  //   1. RESTORE — if the source bid ALREADY has a persisted row for
  //      this aircraft_id (regardless of its current shortlisted flag),
  //      reuse that row instead of POSTing a new one. Covers:
  //        - soft-deleted match (edit mode's classic add-delete-add
  //          zombie fix)
  //        - same-session remove-then-readd (trip-group or edit): the
  //          source snapshot still shows the option as shortlisted:true
  //          even though the broker filtered it out locally, so the
  //          old shortlisted:false guard missed this case and produced
  //          a duplicate DB sibling with the same aircraft_id.
  //      Pricing fields are RESET to 0 on RESTORE — the broker asked to
  //      re-add the tail fresh; carrying over stale pricing from the
  //      pre-remove state is surprising.
  //   2. NEW — the source bid has never seen this aircraft. Placeholder
  //      id + _isNew marker so the nested-write INSERTs a fresh row.
  const handleAppendBidOption = React.useCallback((hydratedAircraft) => {
    if (!hydratedAircraft) return;
    const mapped = mapAircraftToPayload(hydratedAircraft);
    const aircraftId = mapped.aircraft_id;

    // Source options — where they live depends on the flow:
    //   - Edit mode (?quoteid=…): the loaded source quote's bid.
    //   - Trip Group: the shortlist-page bid captured on entry.
    //   - Quick Quote: neither is present → this always no-ops there.
    const sourceOptions =
      editSource?.bid_items?.[0]?.bid?.bid_options
      || selectedTripGroup?.bids?.[0]?.bid_options
      || [];
    const existingRow = sourceOptions.find(
      (o) => o?.aircraft_id === aircraftId && o?.bid_option_id,
    );

    let optionToAppend;
    if (existingRow) {
      // RESTORE — reuse the DB row. Keep its real bid_option_id so the
      // save PATCHes the existing row (no zombie sibling). Reset every
      // pricing / tax / fee field to 0 so the drawer opens on a clean
      // slate — matches the product ask: "re-add the same aircraft →
      // reset fees to 0 or default". Legacy carryover of pricing from
      // a soft-deleted state used to surprise brokers on re-add.
      optionToAppend = {
        ...existingRow,
        shortlisted: true,
        status: 'SENT',
        base_price: 0,
        fuel_cost: 0,
        landing_fees: 0,
        crew_fees: 0,
        catering_cost: 0,
        ground_transport: 0,
        other_fees: 0,
        total_price: 0,
        broker_markup: 0,
        final_price: 0,
        operator_cost: 0,
        federal_excise_tax: 0,
        segment_tax: 0,
        international_tax: 0,
        platform_fee: 0,
        credit_card_fee: 0,
        // Rate columns — clearing forces the drawer's back-solve to
        // fall through to its defaults ($5.20/pax segment fee, $22.90
        // per-pax intl facility tax) on next open.
        segment_fee_per_leg: null,
        international_facilities_fee_per_pax: null,
      };
    } else {
      // NEW path — placeholder id + _isNew marker.
      optionToAppend = {
        ...mapped,
        bid_option_id: `new-${aircraftId}-${Date.now()}`,
        _isNew: true,
      };
    }

    updateBidOptions((prev) => [...(prev || []), optionToAppend]);
  }, [updateBidOptions, editSource, selectedTripGroup]);

  // Wrap the hook's removeBidOption so it also releases the removed
  // aircraft_id from shortlistProcessedIdsRef (so re-adding the same
  // tail via SearchAircraft isn't a silent no-op) AND drops it from
  // `shortlistAircraft` on the appStore. That second step matters in
  // Quick Quote flow, where the shortlist deliberately stays populated
  // (processQuote uses `shortlistAircraft.length > 0` as its Path 1
  // routing signal). Without it, the shortlist-consumer effect below
  // — which re-runs on formData.bidOptions changes — would see the
  // aircraft still in the shortlist, no longer in the ref, and no
  // longer in bidOptions, and immediately re-append it. Result:
  // Remove looked like a no-op.
  const handleRemoveBidOption = React.useCallback((optionId) => {
    const removed = (formData.bidOptions || []).find(
      (o) => o?.bid_option_id === optionId,
    );
    const removedAircraftId =
      removed?.aircraft?.aircraft_id || removed?.aircraft_id;
    if (removedAircraftId) {
      shortlistProcessedIdsRef.current.delete(removedAircraftId);
      removeFromShortlist(removedAircraftId);
    }
    removeBidOption(optionId);
  }, [formData.bidOptions, removeBidOption, removeFromShortlist]);

  // Post-initial-load shortlist consumer — SearchAircraft's "Add Tail"
  // flow puts picks into useAppStore().shortlistAircraft. Runs in every
  // mode (edit-quote / duplicate / trip-group / quick-quote) so newly
  // shortlisted tails always append to formData.bidOptions rather than
  // triggering a reload that clobbers pre-existing tails.
  //
  // Skips items already integrated (initial load stamped their
  // aircraft_ids into shortlistProcessedIdsRef; items still on
  // formData.bidOptions are also excluded). Consumed items get
  // shortlist-cleared once appended so they don't re-add on re-render.
  useEffect(() => {
    // Wait until the initial load (or the edit-mode hydration) has
    // settled — otherwise we'd race the initial load in Quick Quote
    // mode where allBidOptions comes from the shortlist itself.
    if (!editQuoteId && !initialLoadDoneRef.current) return;
    if (!Array.isArray(shortlistAircraft) || shortlistAircraft.length === 0) return;
    const existingAircraftIds = new Set(
      (formData.bidOptions || [])
        .map((opt) => opt?.aircraft?.aircraft_id || opt?.aircraft_id)
        .filter(Boolean),
    );
    let appended = false;
    shortlistAircraft.forEach((item) => {
      const aid = item?.aircraft_id || item?.aircraft?.aircraft_id;
      if (!aid) return;
      if (shortlistProcessedIdsRef.current.has(aid)) return;
      if (existingAircraftIds.has(aid)) return;
      shortlistProcessedIdsRef.current.add(aid);
      handleAppendBidOption(item);
      appended = true;
    });
    // Only clear the shortlist store when we're in edit / duplicate /
    // trip-group mode. In Quick Quote, `processQuote` uses
    // `shortlistAircraft.length > 0` as the routing signal for Path 1
    // (create fresh bid + POST bid_options). Clearing here would push
    // the save into Path 2 (updateExistingBidOptions → PATCH by
    // placeholder bid_option_id → 404). The `shortlistProcessedIdsRef`
    // guard above prevents re-appending the same items on re-render,
    // so leaving the shortlist populated is safe in Quick Quote.
    if (appended && (editQuoteId || selectedTripGroup)) {
      clearShortlist();
    }
  }, [shortlistAircraft, editQuoteId, selectedTripGroup, handleAppendBidOption, clearShortlist, formData.bidOptions]);

  // Open preview (validates form first).
  // Uses `getValidationIssues` which re-runs the real validators (bypasses
  // the `manuallyCompleted` UX shortcut). If anything fails, surface a
  // themed modal that lists the issues and offers a one-click jump to the
  // first failing section.
  const handleOpenPreview = () => {
    const issues = getValidationIssues();
    if (issues.length > 0) {
      setValidationModal({ open: true, issues });
      return;
    }
    // Legacy guard kept for safety — should be a no-op now.
    const errors = getFormErrors();
    const firstError = Object.values(errors)[0];
    if (firstError) {
      setError(firstError || 'Please complete all sections before previewing');
      setTimeout(() => setError(null), 5000);
      return;
    }
    setPreviewOpen(true);
  };

  // Modal action: close + force-open the failing section so the broker can
  // fix the field immediately. Scrolling into view is handled by the
  // section's own collapse/expand animation.
  const handleJumpToSection = (section) => {
    setValidationModal({ open: false, issues: [] });
    if (section) openSection(section);
  };

  // Handle customize modal confirm - update settings and send quote
  const handleCustomizeConfirm = (customizedData) => {
    const updatedSettings = {
      ...formData.settings,
      ...customizedData,
    };
    updateSettings(updatedSettings);
    setCustomizeModalOpen(false);
    setPreviewOpen(true);

    const quoteData = {
      ...formData,
      settings: updatedSettings,
      flights: formData.flights.flights,
      flightType: formData.flights.type,
    };
    setLastSubmitMode('send');
    // In edit mode, route to the PATCH-and-notify path so we update the
    // existing quote instead of creating a duplicate.
    if (isEditing) {
      handleUpdateQuoteAndNotify(quoteData);
    } else {
      handleSendQuote(quoteData);
    }
  };

  // Junior Broker gets two buttons in the preview: "Save Draft" (private,
  // status=DRAFT) and "Submit for Approval" (status=PENDING_APPROVAL, shows
  // up in Admin's Pending Approvals list). Other roles keep the single
  // Save Draft + Send Quote flow.
  const isJuniorBroker = normalizeRole(userProfile?.role) === ROLES.JUNIOR_BROKER;
  const editSourceStatusU = (editSource?.status || '').toUpperCase();
  // Submit for Approval visible: new quote OR editing a DRAFT. Editing a
  // PENDING_APPROVAL quote already submitted — Save keeps it there.
  const showSubmitForApproval = isJuniorBroker && (!isEditing || editSourceStatusU === 'DRAFT');

  // Wrap handlers to also close preview. In edit mode, "Save Draft" must
  // PATCH the existing quote — calling `handleSaveDraft` would POST a new
  // /quotes record and orphan the original. `handleUpdateQuote` preserves
  // the current status (DRAFT stays DRAFT) and does the silent save.
  const onSaveDraft = async (quoteData) => {
    setLastSubmitMode('draft');
    if (isEditing) {
      await handleUpdateQuote(quoteData);
    } else {
      await handleSaveDraft(quoteData);
    }
    setPreviewOpen(false);
  };

  // JB "Submit for Approval". Two paths:
  //   - New quote → handleSubmitForApproval (POST with status=PENDING_APPROVAL).
  //   - Editing a DRAFT → PATCH the edits AND promote status to PENDING_APPROVAL
  //     in ONE atomic PATCH — a failure aborts the whole flow (surfaces the
  //     error toast, no navigation), so we never end up half-updated with a
  //     stale status. Not reachable for other source statuses (button is
  //     gated in the preview).
  const onSubmitForApproval = async (quoteData) => {
    setLastSubmitMode('approval');
    if (isEditing) {
      const shouldPromote = editSourceStatusU === 'DRAFT';
      await handleUpdateQuote(
        quoteData,
        shouldPromote ? { targetStatus: 'PENDING_APPROVAL' } : {},
      );
    } else {
      await handleSubmitForApproval(quoteData);
    }
    setPreviewOpen(false);
  };

  // Edit-mode submit. The preview's "Send" button routes here instead of
  // opening the customize modal — edits commit in place, no resend dialog.
  const onSaveEdit = async () => {
    const quoteData = {
      ...formData,
      flights: formData.flights.flights,
      flightType: formData.flights.type,
    };
    await handleUpdateQuote(quoteData);
    setPreviewOpen(false);
  };


  return (
    <div className="generate-quote-page pb-24 w-full lg:max-w-[1000px] bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      <main className="px-1 py-3">
        {isEditing && editSource?.quote_number && (
          <div className="flex items-center justify-between gap-2 mb-4">
            <h4 className="text-gray-500 bg-white rounded-lg px-3 py-1">
              Editing Quote: <span className="text-primary-500 font-semibold">{editSource.quote_number}</span>
            </h4>
            {editSource?.status && (() => {
              const config = getQuoteStatusConfig(getEffectiveQuoteStatus(editSource));
              const Icon = config.icon;
              return (
                <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {config.label}
                </span>
              );
            })()}
          </div>
        )}
        {!isEditing && selectedTripGroup?.tripNumber &&
          <div className="text-right mb-4">
            <h4 className="text-gray-500 inline bg-white rounded-lg px-3 py-1">Trip: <span className="text-primary-500 font-semibold">{selectedTripGroup.tripNumber}</span></h4>
          </div>}
        {/* Edit-mode loading gate. Blocks the form while we either fetch the
            source quote OR wait for the airports cache to be available for
            enrichment. `isEditing` flips true only after `loadFromQuote` has
            populated formData. */}
        {editQuoteId && !isEditing ? (
          <div className="space-y-4" aria-busy="true" aria-live="polite">
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-600">
              <RiLoader4Line className="w-4 h-4 animate-spin text-primary-600" />
              <span>Loading quote…</span>
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 w-5 rounded" />
                  <Skeleton className="h-5 w-40" />
                </div>
                <Skeleton className="h-10 w-full rounded-lg" />
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-10 w-full rounded-lg" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="space-y-4">
          {error && (
            <div
              role="alert"
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
            >
              <p className="text-sm font-medium">
                {isEditing
                  ? 'Unable to save changes to this quote at this time. This may be a temporary system issue. Please retry, and contact support if it continues.'
                  : lastSubmitMode === 'draft'
                    ? 'Unable to save this draft at this time. This may be a temporary system issue. Please retry, and contact support if it continues.'
                    : lastSubmitMode === 'approval'
                      ? 'Unable to submit this quote for approval at this time. This may be a temporary system issue. Please retry, and contact support if it continues.'
                      : 'Unable to generate a new quote at this time. This may be due to a temporary system issue. Please retry resending quote, and contact support if it continues.'}
              </p>

            <button
            type="button"
            onClick={() => {
              const quoteData = {
                ...formData,
                flights: formData.flights.flights,
                flightType: formData.flights.type,
              };
              if (isEditing) {
                // Retry the silent PATCH path. The broker can still trigger
                // a re-send via the preview's "Send Quote to Client" button
                // if they want the client to receive the revised proposal.
                onSaveEdit();
              } else if (lastSubmitMode === 'draft') {
                // Continue the draft save. The processor resumes from the failed
                // step (it won't re-create the bid / bid_options).
                onSaveDraft(quoteData);
              } else if (lastSubmitMode === 'approval') {
                // Continue the approval submission — must NOT fall through to
                // onSaveDraft or the retry would silently downgrade the status
                // from PENDING_APPROVAL to DRAFT.
                onSubmitForApproval(quoteData);
              } else {
                setCustomizeModalOpen(true);
              }
            }}
            className="px-5 py-1 mt-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
          >
            {isEditing || lastSubmitMode === 'draft' || lastSubmitMode === 'approval' ? 'Try Again' : 'Resend Quote'}
          </button>
            </div>
          )}

          <ClientInformationSection
            data={formData.client}
            onUpdate={updateClient}
            isComplete={completedSections.client}
            isExpanded={expandedSections.client}
            onToggle={() => toggleSection('client')}
            onNext={() => goToNextSection('client')}
            locked={isClientEditLocked}
            lockedReason="The quote has already been sent — the client cannot be changed."
          />

          <FlightDetailsSection
            data={formData.flights}
            onUpdate={updateFlights}
            isComplete={completedSections.flights}
            isExpanded={expandedSections.flights}
            onToggle={() => toggleSection('flights')}
            onNext={() => goToNextSection('flights')}
            onBack={() => goToPreviousSection('flights')}
            isClientComplete={completedSections.client}
            isUnlocked={unlockedSections.flights}
          />

          <AircraftOptionsSection
            formData={formData}
            isComplete={completedSections.aircraft}
            isExpanded={expandedSections.aircraft}
            onToggle={() => toggleSection('aircraft')}
            onNext={handleAircraftNext}
            onBack={() => goToPreviousSection('aircraft')}
            isUnlocked={unlockedSections.aircraft}
            onUpdate={updateBidOptionProperty}
            onBatchUpdate={updateBidOptionProperties}
            onRemove={handleRemoveBidOption}
            onSaveAircraftProfile={updateAircraftProfile}
            onSaveOperatorNameOverride={updateOperatorNameOverride}
            isEditing={isEditing}
            editSourceStatus={editSource?.status}
            onAppendBidOption={handleAppendBidOption}
            isDataLoading={isDataLoading}
          />

          <QuoteSettingsSection
            settings={formData.settings}
            onUpdate={updateSettings}
            isComplete={completedSections.settings}
            isExpanded={expandedSections.settings}
            onToggle={handleSettingsToggle}
            onPreview={handleOpenPreview}
            onBack={() => goToPreviousSection('settings')}
            isAircraftComplete={completedSections.aircraft}
            isUnlocked={unlockedSections.settings}
            flightOptions={formData.bidOptions}
            defaultContractUrl={resolveAssetUrl(organization?.branding?.contract)}
            // Mobile-parity naming inputs — the section renders
            // "{orgName}.pdf" for the org default and "{quoteNumber}.pdf"
            // for a per-quote contract that differs from the org default.
            orgName={organization?.name || ''}
            orgContractRawPath={organization?.branding?.contract || ''}
            orgContractHistory={Array.isArray(organization?.branding?.contractHistory)
              ? organization.branding.contractHistory
              : []}
            quoteNumber={editSource?.quote_number || formData?.settings?.quoteNumber || ''}
            isEditing={isEditing}
          />
        </div>
        )}
      </main>

      <QuoteCustomizeModal
        isOpen={customizeModalOpen}
        onClose={() => setCustomizeModalOpen(false)}
        settings={formData.settings}
        clientData={formData.client?.clientData}
        onConfirm={handleCustomizeConfirm}
        bidOptions={formData.bidOptions}
      />

      <QuotePreview
        isOpen={previewOpen}
        onClose={() => !isSending && !isSavingDraft && setPreviewOpen(false)}
        quoteData={{
          ...formData,
          flights: formData.flights.flights,
          flightType: formData.flights.type
        }}
        onSend={isEditing
          ? () => {
              // DRAFT or PENDING_APPROVAL in edit mode = first-time send:
              // skip the "Resend?" prompt and go straight to the customize
              // /email composer. (PENDING_APPROVAL means admin is approving
              // a JB submission — semantically the same as first send.)
              const sourceStatus = (editSource?.status || '').toUpperCase();
              const isFirstSend =
                !sourceStatus || sourceStatus === 'DRAFT' || sourceStatus === 'PENDING_APPROVAL';
              if (isFirstSend) {
                // Close the preview drawer before opening the customize modal —
                // the drawer's focus trap would otherwise block the modal's
                // subject/body inputs from receiving focus (not editable).
                setPreviewOpen(false);
                setCustomizeModalOpen(true);
              } else {
                setEditSendConfirmOpen(true);
              }
            }
          : () => { setPreviewOpen(false); setCustomizeModalOpen(true); }}
        onSaveDraft={onSaveDraft}
        onSubmitForApproval={showSubmitForApproval ? onSubmitForApproval : undefined}
        isSending={isSending}
        isSavingDraft={isSavingDraft}
        isEditing={isEditing}
        hideSaveDraft={isEditing && !['DRAFT', 'PENDING_APPROVAL'].includes((editSource?.status || '').toUpperCase())}
        quote={editSource}
      />

      {/* Edit-mode Send confirmation. Broker chooses whether to push the
          revised quote to the client (opens the customize/email composer
          and PATCHes via handleUpdateQuoteAndNotify), or just save silently
          (PATCH only via onSaveEdit). */}
      <Modal
        isOpen={editSendConfirmOpen}
        onClose={() => !isSending && setEditSendConfirmOpen(false)}
        title="Resend the updated quote?"
        size="sm"
        closeOnOutsideClick={!isSending}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
              {isSending
                ? <RiLoader4Line className="w-5 h-5 text-primary-600 animate-spin" />
                : <RiErrorWarningLine className="w-5 h-5 text-primary-600" />}
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Do you want to <span className="font-semibold">resend the email</span> to
              the client with the updated quote, or <span className="font-semibold">just save</span>
              {' '}the changes silently?
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setEditSendConfirmOpen(false)}
              disabled={isSending}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                setEditSendConfirmOpen(false);
                await onSaveEdit();
              }}
              disabled={isSending}
              className="px-4 py-2 text-sm text-gray-700 rounded-full border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Just save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditSendConfirmOpen(false);
                // Close the preview drawer so its focus trap doesn't block the
                // customize modal's subject/body inputs.
                setPreviewOpen(false);
                setCustomizeModalOpen(true);
              }}
              disabled={isSending}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Resend email
            </button>
          </div>
        </div>
      </Modal>

      {/* Validation modal — opens when the broker clicks Preview but a section
          still has missing/invalid fields. Lists each issue and offers a
          one-click jump to the first failing section. */}
      <Modal
        isOpen={validationModal.open}
        onClose={() => setValidationModal({ open: false, issues: [] })}
        title="Some fields need updating"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
              <RiErrorWarningLine className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              The following needs to be updated before you can preview this quote:
            </p>
          </div>

          <ul className="space-y-3 ml-13">
            {validationModal.issues.map((issue) => (
              <li
                key={issue.section}
                className="border border-gray-200 rounded-xl p-3 bg-gray-50"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900">{issue.label}</span>
                  <button
                    type="button"
                    onClick={() => handleJumpToSection(issue.section)}
                    className="text-xs px-3 py-1 rounded-full bg-primary-700 text-white hover:bg-primary-800 transition-colors"
                  >
                    Open
                  </button>
                </div>
                <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">
                  {issue.messages.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setValidationModal({ open: false, issues: [] })}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
            >
              Close
            </button>
            {validationModal.issues[0] && (
              <button
                type="button"
                onClick={() => handleJumpToSection(validationModal.issues[0].section)}
                className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800"
              >
                Fix {validationModal.issues[0].label}
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Pricing inconsistency confirm — fires on Next from Aircraft
          section when any bid_option's final_price doesn't match the
          forward-calc result. On OK we patch each inconsistent option
          in-place (recomputed FET/segment/intl/final_price in formData)
          and keep the broker on the Aircraft section so they can
          review. Second Next click proceeds normally because
          pricingAcknowledged is true. */}
      <Modal
        isOpen={pricingInconsistencyOpen}
        onClose={() => setPricingInconsistencyOpen(false)}
        title="Pricing needs an update"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
              <RiErrorWarningLine className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 leading-relaxed">
                The pricing on this quote is out of date. Clicking OK will
                update the <span className="font-semibold">Final Price</span>
                {' '}to reflect the applicable taxes and fees for
                {' '}
                <span className="font-semibold">
                  {inconsistentOptionIds.length}
                </span>
                {' '}
                aircraft option{inconsistentOptionIds.length === 1 ? '' : 's'}.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mt-2">
                Review the updated Aircraft Pricing before continuing — no
                changes are saved until you send or save the quote.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setPricingInconsistencyOpen(false)}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyPricingRecompute}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800"
            >
              OK
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GenerateQuote;
